import { Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { 
  Entity, 
  EntityCreateRequest, 
  EntityStatus, 
  EntityJurisdiction, 
  EntityRegisteredAgent, 
  EntityGoverningCountry, 
  EntityType,
  EntityGroupAccount
} from '../entities.model';
import { EntitiesService } from '../entities.service';

@Component({
    selector: 'app-entity-form',
    templateUrl: './entity-form.component.html',
    styleUrls: ['./entity-form.component.scss'],
    standalone: false
})
export class EntityFormComponent implements OnInit, OnDestroy, OnChanges {

  @Input() entity: Entity | null = null;
  @Input() visible: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() title: string = 'Add Entity';
  @Input() isTaskContext: boolean = false; // True when called from task grid, false from entities grid
  @Input() basicEditMode: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() entitySaved = new EventEmitter<Entity>();
  @Input() skipIsTKUpdatedEntity: boolean = false; // True if entity has been updated by TK, false otherwise

  entityForm: UntypedFormGroup;
  isLoading: boolean = false;
  isSubmitting: boolean = false;

  // Dropdown options
  jurisdictions: EntityJurisdiction[] = [];
  registeredAgents: EntityRegisteredAgent[] = [];
  private dropdownsLoaded: boolean = false;
  private _isEntityInfoComplete: boolean = false;
  governingCountries: EntityGoverningCountry[] = [];
  entityTypes: EntityType[] = [];
  groupAccounts: EntityGroupAccount[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private entitiesService: EntitiesService,
    private messageService: MessageService
  ) {
    this.entityForm = this.createForm();
  }

  ngOnInit(): void {
    // Don't load dropdown data here - load it lazily when dialog opens
    if (this.entity) {
      this.populateForm();
      this.configureFieldAccess();
    }
  }

  // Show loader when entity is being loaded
  setLoading(val: boolean) {
    this.isLoading = val;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue) {
      this.messageService.clear('entityFormToast');
      // Dialog is opening - load dropdowns only if not in read-only mode and not already loaded
      if (!this.readOnly && !this.dropdownsLoaded) {
        this.loadDropdownData();
        this.dropdownsLoaded = true;
      }
      
      // Ensure loading is false if entity is already provided
      if (this.entity) {
        this.isLoading = false;
        this.populateForm();
        this.configureFieldAccess();
      } else {
        // No entity provided (Add New Entity case)
        this.isLoading = false;
        this.entityForm.reset();
        this.configureFieldAccess();
      }
    }
    
    // Recalculate entity info complete status when inputs change
    if (changes['entity'] || changes['title'] || changes['isTaskContext']) {
      this._isEntityInfoComplete = this.calculateEntityInfoComplete();
    }
    
    if (changes['entity'] && !changes['visible']) {
      console.log('Entity changed:', changes['entity'].currentValue);
      if (changes['entity'].currentValue) {
        this.isLoading = false;
        this.populateForm();
      } else {
        this.isLoading = false;
        this.entityForm.reset();
      }
      this.configureFieldAccess();
    }
  }

  private configureFieldAccess(): void {
    // If readOnly is true, disable all fields regardless of title
    if (this.readOnly) {
      console.log('Read-only mode - disabling all fields');
      Object.keys(this.entityForm.controls).forEach(key => {
        this.entityForm.get(key)?.disable();
      });
      return;
    }

    // If this is "Update Entity Information" or "Show Entity Information" dialog
    if (this.title === 'Update Entity Information' || this.title === 'Show Entity Information') {
      console.log('Configuring field access for', this.title);
      console.log('Entity:', this.entity);
      console.log('isTkUpdatedEntity:', this.entity?.isTkUpdatedEntity);
      console.log('isTaskContext:', this.isTaskContext);
      
      // Check if the 3 specific fields are empty
      const formationDateEmpty = !this.entity?.formationDate;
      const authorizationDateEmpty = !this.entity?.authorizationDate;
      const jurisdictionIdEmpty = !this.entity?.jurisdictionIdNumber;
      const tkEditableFieldsEmpty = formationDateEmpty || authorizationDateEmpty || jurisdictionIdEmpty;
      
      // TK-specific logic: If in task context and any of the 3 fields are empty, allow TK to edit only those fields
      if (this.isTaskContext && tkEditableFieldsEmpty) {
        console.log('TK mode - enabling only empty fields among: formationDate, authorizationDate, jurisdictionIdNumber');
        // Disable all fields first
        Object.keys(this.entityForm.controls).forEach(key => {
          this.entityForm.get(key)?.disable();
        });
        
        // Enable only the empty fields
        if (formationDateEmpty) {
          this.entityForm.get('formationDate')?.enable();
        }
        if (authorizationDateEmpty) {
          this.entityForm.get('authorizationDate')?.enable();
        }
        if (jurisdictionIdEmpty) {
          this.entityForm.get('jurisdictionIdNumber')?.enable();
        }
      } else {
        // Non-TK logic: Check if entity has associated projects or tasks
        const hasAssociations = (this.entity?.associatedProjectIds && this.entity.associatedProjectIds.length > 0) ||
                                (this.entity?.associatedTaskIds && this.entity.associatedTaskIds.length > 0);
        
        // Check if entity information is already complete (isTkUpdatedEntity === 'Yes')
        if (this.entity?.isTkUpdatedEntity === 'Yes' || hasAssociations) {
          // Entity info is complete OR has associations, disable all fields (Show mode)
          console.log('Entity info COMPLETE or HAS ASSOCIATIONS - disabling all fields');
          Object.keys(this.entityForm.controls).forEach(key => {
            this.entityForm.get(key)?.disable();
          });
        } else {
          // Enable all fields (regular Update mode from Entities grid)
          console.log('Entity info NOT complete and NO associations - enabling all fields');
          Object.keys(this.entityForm.controls).forEach(key => {
            if (key !== 'id' && key !== 'subjectEntityId') {
              this.entityForm.get(key)?.enable();
            }
          });
        }

       
      }
      //this.skipIsTKUpdatedEntity &&
       if( (this.entity?.isTkUpdatedEntity != 'Yes'|| this.entity?.isTkUpdatedEntity==null)&& !this.entity?.subjectEntityId) {
         this.entityForm.enable();
         this.entityForm.get('subjectEntityId')?.disable();
         this.entityForm.get('id')?.disable();
        }
        if(!this.skipIsTKUpdatedEntity){
          this.entityForm.get('jurisdictionIdNumber')?.setValidators(Validators.required);
          this.entityForm.get('jurisdictionIdNumber')?.updateValueAndValidity();
          this.entityForm.get('authorizationDate')?.setValidators(Validators.required);
          this.entityForm.get('authorizationDate')?.updateValueAndValidity();
          this.entityForm.get('formationDate')?.setValidators(Validators.required);
          this.entityForm.get('formationDate')?.updateValueAndValidity();
        }
    } else {
      // Enable all fields for regular add/edit operations
      Object.keys(this.entityForm.controls).forEach(key => {
        if (key !== 'id' && key !== 'subjectEntityId') {
          this.entityForm.get(key)?.enable();
        }
      });
    }
    if(this.basicEditMode && !this.readOnly){
      Object.keys(this.entityForm.controls).forEach(key => {
        if (key !== 'id' && key !== 'subjectEntityId' && key !=='name') {
          this.entityForm.get(key)?.enable();
          this.entityForm.get(key)?.clearValidators();
          this.entityForm.get(key)?.updateValueAndValidity();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(300)]],
      groupAccountId: [null, Validators.required],
      governingCountryId: [null, Validators.required],
      domesticJurisdictionId: [null, Validators.required],
      formationDate: [''],
      entityTypeId: [null, Validators.required],
      jurisdictionId: [null, Validators.required],
      authorizationDate: [''],
      jurisdictionIdNumber: ['', Validators.maxLength(200)],
      registeredAgentId: [null, Validators.required],
      statusId: [null],
      legalIdentifier: [''],
      fein: [''],
      fiscalEndYear: [''],
      subjectEntityId: ['']
    });
  }

  private loadDropdownData(): void {
    this.isLoading = true;
    
    // Load all dropdown data in parallel
    const loadSubscriptions = [
      // this.entitiesService.getEntityStatusForDropdown(),
      this.entitiesService.getEntityJurisdictionForDropdown(),
      this.entitiesService.getEntityRegisteredAgentForDropdown(),
      this.entitiesService.getEntityGoverningCountryForDropdown(),
      this.entitiesService.getEntityTypeForDropdown(),
      this.entitiesService.getEntityGroupAccountForDropdown()
    ];

    Promise.all(loadSubscriptions.map(obs => obs.toPromise()))
      .then(([jurisdictionRes, agentRes, countryRes, typeRes, groupAccountRes]) => {
        this.jurisdictions = jurisdictionRes?.data || [];
        this.registeredAgents = agentRes?.data || [];
        this.governingCountries = countryRes?.data || [];
        this.entityTypes = typeRes?.data || [];
        this.groupAccounts = groupAccountRes?.data || [];
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error loading dropdown data:', error);
        this.messageService.add({
          key: 'entityFormToast',
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load form data',
          life: 20000
        });
        this.isLoading = false;
      });
  }

  private populateForm(): void {
    if (this.entity) {
      this.entityForm.patchValue({
        id: this.entity.id,
        name: this.entity.name,
        groupAccountId: this.entity.groupAccountId,
        governingCountryId: this.entity.governingCountryId,
        domesticJurisdictionId: this.entity.domesticJurisdictionId,
        formationDate: this.entity.formationDate ? this.formatDateForInput(this.entity.formationDate) : '',
        entityTypeId: this.entity.entityTypeId,
        jurisdictionId: this.entity.jurisdictionId,
        authorizationDate: this.entity.authorizationDate ? this.formatDateForInput(this.entity.authorizationDate) : '',
        jurisdictionIdNumber: this.entity.jurisdictionIdNumber,
        registeredAgentId: this.entity.registeredAgentId,
        statusId: this.entity.statusId,
        legalIdentifier: this.entity.legalIdentifier,
        fein: this.entity.fein,
        fiscalEndYear: this.entity.fiscalEndYear,
        subjectEntityId: this.entity.subjectEntityId
      });
    }
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) {
      return '';
    }

    const value = String(dateString).trim();
    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
    }

    const usDateMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usDateMatch) {
      const month = usDateMatch[1].padStart(2, '0');
      const day = usDateMatch[2].padStart(2, '0');
      return `${usDateMatch[3]}-${month}-${day}`;
    }

    return '';
  }

  private formatDateForSubmit(dateString: string): string {
    if (!dateString) return '';
    const value = String(dateString).trim();
    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateMatch) {
      return `${isoDateMatch[2]}/${isoDateMatch[3]}/${isoDateMatch[1]} 00:00:00`;
    }
    return value;
  }

  /**
   * Validate dates according to business rules
   */
  private validateDates(): string | null {
    const formationDate = this.entityForm.get('formationDate')?.value;
    const authorizationDate = this.entityForm.get('authorizationDate')?.value;
    const domesticJurisdictionId = this.entityForm.get('domesticJurisdictionId')?.value;
    const jurisdictionId = this.entityForm.get('jurisdictionId')?.value;

    if (!formationDate || !authorizationDate) {
      return null; // Skip validation if dates are not provided
    }

    const formation = new Date(formationDate);
    const authorization = new Date(authorizationDate);

    // Authorization date must be on or after formation date
    if (authorization < formation) {
      return 'Date of Authorization must be on the same day or after the Date of Formation.';
    }

    // If domestic jurisdiction is the same as jurisdiction, dates must match
    if (domesticJurisdictionId === jurisdictionId) {
      if (formation.getTime() !== authorization.getTime()) {
        return 'When Domestic Jurisdiction is the same as Jurisdiction, Date of Authorization must match Date of Formation.';
      }
    }

    return null;
  }

  /**
   * Check if required fields are completed (for LPA assignment validation)
   */
  areRequiredFieldsCompleted(): boolean {
    const requiredFields = ['name', 'groupAccountId', 'governingCountryId', 'domesticJurisdictionId', 'entityTypeId', 'jurisdictionId', 'registeredAgentId'];
    return requiredFields.every(field => {
      const value = this.entityForm.get(field)?.value;
      return value !== null && value !== undefined && value !== '';
    });
  }

  /**
   * Save entity
   */
  saveEntity(): void {
    // For Update Entity Information, only validate enabled fields
    let formValid = true;
    if (this.title === 'Update Entity Information') {
      // Only check validation for enabled fields
      const enabledControls = Object.keys(this.entityForm.controls).filter(key => 
        this.entityForm.get(key)?.enabled
      );
      formValid = enabledControls.every(key => this.entityForm.get(key)?.valid);
    } else {
      formValid = this.entityForm.valid;
    }
    
    if (!formValid) {
      this.markFormGroupTouched();
      this.messageService.add({
        key: 'entityFormToast',
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: 20000

      });
      return;
    }

    // Validate dates
    const dateError = this.basicEditMode ? null : this.validateDates();
    if (dateError) {
      this.messageService.add({
        key: 'entityFormToast',
        severity: 'error',
        summary: 'Date Validation Error',
        detail: dateError,
        life: 20000
      });
      return;
    }

    this.isSubmitting = true;
    const formValue = this.entityForm.getRawValue(); // Use getRawValue to include disabled fields

    const entityRequest: EntityCreateRequest = {
      id: formValue.id,
      name: formValue.name,
      groupAccountId: formValue.groupAccountId,
      governingCountryId: formValue.governingCountryId,
      domesticJurisdictionId: formValue.domesticJurisdictionId,
      formationDate: this.formatDateForSubmit(formValue.formationDate),
      entityTypeId: formValue.entityTypeId,
      jurisdictionId: formValue.jurisdictionId,
      authorizationDate: this.formatDateForSubmit(formValue.authorizationDate),
      jurisdictionIdNumber: formValue.jurisdictionIdNumber,
      registeredAgentId: formValue.registeredAgentId,
      statusId: formValue.statusId,
      legalIdentifier: formValue.legalIdentifier,
      fein: formValue.fein,
      fiscalEndYear: formValue.fiscalEndYear,
      subjectEntityId: formValue.subjectEntityId,
      // Only include isTkUpdatedEntity when updating from task context
      isTkUpdatedEntity: !this.skipIsTKUpdatedEntity && (this.isTaskContext && this.title === 'Update Entity Information') ? 'Yes' : undefined
    };

    const sub = this.entitiesService.saveEntity(entityRequest)
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.messageService.add({
              key: 'entityFormToast',
              severity: 'success',
              summary: 'Success',
              detail: this.entity ? 'Entity updated successfully' : 'Entity created successfully',
              life: 20000
            });
            this.entitySaved.emit(response.data);
            this.closeDialog();
          }
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error saving entity:', error);
          
          // Check if error response contains a specific message (e.g., duplicate entity)
          const errorMessage = error?.error?.message || error?.message || 'Failed to save entity';
          
          this.messageService.add({
            key: 'entityFormToast',
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 20000
          });
          this.isSubmitting = false;
        }
      });

    this.subscriptions.push(sub);
  }

  /**
   * Mark all form controls as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.entityForm.controls).forEach(key => {
      const control = this.entityForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Close dialog
   */
  closeDialog(): void {
    this.messageService.clear('entityFormToast');
    this.visible = false;
    this.visibleChange.emit(false);
    this.entityForm.reset();
    // Reset the dropdownsLoaded flag so dropdowns load again on next open
    this.dropdownsLoaded = false;
  }

  /**
   * Get field validation error message
   */
  getFieldErrorMessage(fieldName: string): string {
    const control = this.entityForm.get(fieldName);
    if (control?.errors && control?.touched) {
      if (control.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (control.errors['maxlength']) {
        return `${this.getFieldDisplayName(fieldName)} cannot exceed ${control.errors['maxlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  getActionButtonLabel(): string {
    // Check the title to determine the appropriate button label
    if (this.title === 'Update Entity Information' || this.title === 'Show Entity Information') {
      return this.entity ? 'Update' : 'Create Entity';
    } else if (this.entity) {
      return 'Update Entity';
    } else {
      return 'Add';
    }
  }

  isFormInvalid(): boolean {
    if (this.title === 'Update Entity Information' || this.title === 'Show Entity Information') {
      // Only check validation for enabled fields
      const enabledControls = Object.keys(this.entityForm.controls).filter(key => 
        this.entityForm.get(key)?.enabled
      );
      return !enabledControls.every(key => this.entityForm.get(key)?.valid);
    } else {
      return this.entityForm.invalid;
    }
  }

  getSaveDisabledReason(): string {
    if (this.isSubmitting) {
      return 'Saving in progress…';
    }
    if (this.isFormInvalid()) {
      return 'Complete all required fields to save';
    }
    return '';
  }

  get isEntityInfoComplete(): boolean {
    return this._isEntityInfoComplete;
  }
  
  private calculateEntityInfoComplete(): boolean {
    // Check if entity has associated projects or tasks
    const hasAssociations = !!(
      (this.entity?.associatedProjectIds && this.entity.associatedProjectIds.length > 0) ||
      (this.entity?.associatedTaskIds && this.entity.associatedTaskIds.length > 0)
    );
    
    // In task context, check if any of the 3 TK-editable fields are empty
    if (this.isTaskContext && (this.title === 'Update Entity Information' || this.title === 'Show Entity Information')) {
      const formationDateEmpty = !this.entity?.formationDate;
      const authorizationDateEmpty = !this.entity?.authorizationDate;
      const jurisdictionIdEmpty = !this.entity?.jurisdictionIdNumber;
      
      // If any of these fields are empty, entity info is NOT complete (show Update/Cancel buttons)
      if (formationDateEmpty || authorizationDateEmpty || jurisdictionIdEmpty) {
        return false;
      }
    }
    
    // Check if entity information is already complete (isTkUpdatedEntity === 'Yes') OR has associations
    const isComplete = (this.title === 'Update Entity Information' || this.title === 'Show Entity Information') && 
                       (this.entity?.isTkUpdatedEntity === 'Yes' || hasAssociations);
    return isComplete;
  }

  getDropdownClass(fieldName: string): string {
    const control = this.entityForm.get(fieldName);
    const classes = [];
    
    // Add validation class
    if (control?.invalid && control?.touched) {
      classes.push('ng-invalid');
    }
    
    // Add disabled class for styling
    if (control?.disabled) {
      classes.push('dropdown-disabled');
    }
    
    return classes.join(' ');
  }

  private getFieldDisplayName(fieldName: string): string {
    const fieldNames: { [key: string]: string } = {
      'name': 'Entity Name',
      'groupAccountId': 'Group Account',
      'governingCountryId': 'Governing Country',
      'domesticJurisdictionId': 'Domestic Jurisdiction',
      'entityTypeId': 'Entity Type',
      'jurisdictionId': 'Jurisdiction',
      'registeredAgentId': 'Registered Agent',
      'jurisdictionIdNumber': 'Jurisdictional ID Number'
    };
    return fieldNames[fieldName] || fieldName;
  }
}
